import { useParams } from "react-router-dom";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import ParkingLocationPageView from "./ParkingLocationPageView";
import { useRouteContext } from "../../../context/RouteContext";
import { useState } from "react";
import useCustomEffect from "../../../hooks/useCustomEffect";
import useEntityTranslations, {
  mapResponseTranslations,
} from "../../../hooks/useEntityTranslations";

function ParkingLocationPage() {
  const callEvaApi = useEvaApi();
  const { parking_location } = useParams();
  const routeContext = useRouteContext();
  const [submitError, setSubmitError] = useState(null);
  const [parkingLocationData, setParkingLocationData] = useState({
    name: "",
    capacity: 0,
    capacity_for_visitors: 0,
    translations: [],
  });

  const translationsController = useEntityTranslations(
    parkingLocationData.translations,
    onTranslationsChange,
  );

  /**
   * UseEffect to initialize the component
   */
  useCustomEffect(() => {
    if (parking_location) {
      fetchParkingLocationData();
    }
  });

  /**
   * This method will fetch the parking location data from the API
   */
  async function fetchParkingLocationData() {
    await callEvaApi(
      new EvaApiCall(`parking_locations/${parking_location}`)
        .setLoadingGroup("parking_location_page_fetch")
        .setOnSuccess((response) => {
          const newData = response.data.data;
          setParkingLocationData({
            name: newData.name,
            capacity: newData.capacity,
            capacity_for_visitors: newData.capacity_for_visitors,
            translations: mapResponseTranslations(
              newData.translations,
              (translation) => ({
                message: translation.message,
              }),
            ),
          });
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will handle the name change
   * @param {Event} e
   */
  function onNameChange(e) {
    setParkingLocationData((current) => ({
      ...current,
      name: e.target.value,
    }));
  }

  /**
   * This method will handle the capacity change
   * @param {event} e
   */
  function onCapacityChange(e) {
    setParkingLocationData((current) => ({
      ...current,
      capacity: e.target.value,
    }));
  }

  /**
   * This method will handle the capacity for visitors change
   * @param {event} e
   */
  function onCapacityForVisitorsChange(e) {
    setParkingLocationData((current) => ({
      ...current,
      capacity_for_visitors: e.target.value,
    }));
  }

  /**
   * This method will handle the translations change
   * @param {array} value
   */
  function onTranslationsChange(value) {
    setParkingLocationData((current) => ({
      ...current,
      translations: value,
    }));
  }

  /**
   * This method will handle the cancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will handle the submit functionality
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(
        parking_location
          ? `parking_locations/${parking_location}`
          : "parking_locations",
      )
        .setLoadingGroup("parking_location_page_submit")
        .setMethod(parking_location ? "PUT" : "POST")
        .setData(parkingLocationData)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true),
    );
  }

  return (
    <ParkingLocationPageView
      parkingLocationData={parkingLocationData}
      translationsController={translationsController}
      onNameChange={onNameChange}
      onCapacityChange={onCapacityChange}
      onCapacityForVisitorsChange={onCapacityForVisitorsChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default ParkingLocationPage;
